import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useNavigate } from 'react-router-dom';
import { getMyProfile } from '../utils/api';
import {
  ArrowLeftIcon,
  UserIcon,
  BriefcaseIcon,
  EnvelopeIcon,
  UserGroupIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

// Helper component for displaying profile fields
const ProfileField = ({ label, value, fullWidth }) => (
  <div style={{
    padding: '12px 0',
    borderBottom: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: fullWidth ? 'column' : 'row',
    gap: fullWidth ? '4px' : '8px'
  }}>
    <span style={{
      color: 'var(--color-text-muted)',
      minWidth: '140px',
      flexShrink: 0
    }}>
      {label}:
    </span>
    <span style={{ color: 'var(--color-text-primary)' }}>
      {value || '-'}
    </span>
  </div>
);

const MyProfile = () => {
  usePageTitle('My Profile');

  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getMyProfile();
      setProfile(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No employee profile is linked to your account. Please contact HR to set up your profile.');
      } else {
        setError('Failed to load your profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatEmploymentType = (type) => {
    switch (type) {
      case 'employment_contract':
        return 'Employment Contract';
      case 'vendor_contract':
        return 'Vendor Contract';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner" />
          <span>Loading your profile...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-ghost" onClick={() => navigate(-1)}>
            <ArrowLeftIcon style={{ width: 20, height: 20, marginRight: 8 }} />
            Back
          </button>
        </div>
        <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
          <UserIcon style={{ width: 48, height: 48, color: 'var(--color-text-muted)', margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
        </div>
      </div>
    );
  }

  const { employee, employment_history } = profile;

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn btn-ghost" onClick={() => navigate(-1)}>
          <ArrowLeftIcon style={{ width: 20, height: 20, marginRight: 8 }} />
          Back
        </button>
        <h1>My Profile</h1>
      </div>

      {/* Header Card */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'var(--color-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '28px',
            fontWeight: 600
          }}>
            {employee.first_name?.[0]}{employee.last_name?.[0]}
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px' }}>
              {employee.first_name} {employee.last_name}
            </h2>
            {employee.position && (
              <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: '16px' }}>
                {employee.position}
              </p>
            )}
            <p style={{ margin: '4px 0 0', color: 'var(--color-text-muted)', fontSize: '14px' }}>
              {employee.employee_id}
            </p>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <span className={`badge ${employee.is_active ? 'badge-success' : 'badge-danger'}`}>
              {employee.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
        {/* Personal Information */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <UserIcon style={{ width: 20, height: 20 }} />
              Personal Information
            </h3>
          </div>
          <div className="card-body">
            <ProfileField label="First Name" value={employee.first_name} />
            <ProfileField label="Last Name" value={employee.last_name} />
            <ProfileField label="Date of Birth" value={formatDate(employee.date_of_birth)} />
            <ProfileField label="Country" value={employee.country} />
            <ProfileField label="Home Address" value={employee.home_address} />
          </div>
        </div>

        {/* Contact Information */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <EnvelopeIcon style={{ width: 20, height: 20 }} />
              Contact Information
            </h3>
          </div>
          <div className="card-body">
            <ProfileField label="Company Email" value={employee.company_email} />
            <ProfileField label="Personal Email" value={employee.personal_email} />
            <ProfileField label="Phone" value={employee.phone} />
          </div>
        </div>

        {/* Employment Information */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <BriefcaseIcon style={{ width: 20, height: 20 }} />
              Employment Information
            </h3>
          </div>
          <div className="card-body">
            <ProfileField label="Employment Type" value={formatEmploymentType(employee.employment_type)} />
            <ProfileField label="Position" value={employee.position} />
            <ProfileField label="Start Date" value={formatDate(employee.current_start_date)} />
            <ProfileField label="End Date" value={formatDate(employee.current_end_date)} />
          </div>
        </div>

        {/* Manager */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <UserGroupIcon style={{ width: 20, height: 20 }} />
              Reporting
            </h3>
          </div>
          <div className="card-body">
            {employee.manager_name ? (
              <>
                <ProfileField label="Reports To" value={employee.manager_name} />
                {employee.manager_position && (
                  <ProfileField label="Manager Position" value={employee.manager_position} />
                )}
                <ProfileField label="Manager ID" value={employee.manager_employee_id} />
              </>
            ) : (
              <p style={{ color: 'var(--color-text-muted)', margin: 0, padding: '12px 0' }}>No manager assigned</p>
            )}
          </div>
        </div>
      </div>

      {/* Employment History */}
      {employment_history && employment_history.length > 0 && (
        <div className="card" style={{ marginTop: '24px' }}>
          <div className="card-header">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <CalendarIcon style={{ width: 20, height: 20 }} />
              Employment History
            </h3>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {employment_history.map((history) => (
                  <tr key={history.id}>
                    <td>{formatEmploymentType(history.employment_type)}</td>
                    <td>{formatDate(history.start_date)}</td>
                    <td>{history.end_date ? formatDate(history.end_date) : 'Present'}</td>
                    <td>{history.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ marginTop: '24px', padding: '16px', background: 'var(--color-bg-secondary)', borderRadius: '8px', textAlign: 'center' }}>
        <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: '14px' }}>
          To update your profile information, please contact HR.
        </p>
      </div>
    </div>
  );
};

export default MyProfile;
